import { useEffect, useState } from 'react';
import type { PaymentMethodSettings } from '../api.js';
import type { Lang, Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import { Card, PageTitle } from '../components/ui.js';

/**
 * Which ways of being paid a company uses.
 *
 * CFOAI's to set, not the client's. It decides what the payment form offers,
 * and a form listing seven ways to pay where three are meant is a form where
 * the wrong one gets picked — and a method picked wrongly is a figure that
 * will not reconcile against a bank statement later.
 *
 * The list of methods is fixed. A company turns them on and off; it does not
 * invent new ones. That is what keeps the Arabic right, and what keeps a
 * report about how clients pay from having two spellings of "cheque".
 */

export function PaymentMethodsScreen({
  settings,
  busy,
  error,
  lang,
  t,
  onSave,
}: {
  settings: PaymentMethodSettings | null;
  busy: boolean;
  error: string | null;
  lang: Lang;
  t: Translate;
  onSave: (chosen: string[], defaultMethod: string | null) => Promise<void>;
}) {
  const [chosen, setChosen] = useState<string[]>([]);
  const [defaultMethod, setDefaultMethod] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Compared as text, not by identity: every response is a new object.
  const storedKey = JSON.stringify({ c: settings?.chosen ?? [], d: settings?.defaultMethod ?? null });
  useEffect(() => {
    setChosen(settings?.chosen ?? []);
    setDefaultMethod(settings?.defaultMethod ?? null);
  }, [settings?.organisation, storedKey]);

  const currentKey = JSON.stringify({ c: chosen, d: defaultMethod });
  const dirty = storedKey !== currentKey;

  const toggle = (code: string) => {
    setSaved(false);
    setChosen((current) => {
      const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
      // A default that has just been turned off would leave the payment form
      // starting on a method the company does not use.
      if (!next.includes(defaultMethod ?? '')) setDefaultMethod(next[0] ?? null);
      return next;
    });
  };

  const catalogue = settings?.catalogue ?? [];

  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '760px' }}>
      <PageTitle
        title={t('Payment methods')}
        sub={t('The ways this company is paid. These are what the payment form offers.')}
      />
      <div style={{ height: '16px' }} />

      <Card>
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${theme.dividerSoft}`,
            fontWeight: 600,
            fontSize: '13px',
            background: theme.surfaceHeader,
          }}
        >
          {settings?.organisation ?? ''}
        </div>

        <div style={{ padding: '18px 16px' }}>
          <div style={{ fontSize: '12px', color: theme.textMuted, lineHeight: 1.6, marginBottom: '14px' }}>
            {t('Xero has no field for how a payment arrived, so this is kept here and written into the reference Xero is given.')}
          </div>

          <div style={{ display: 'grid', gap: '2px' }}>
            {catalogue.map((method) => {
              const on = chosen.includes(method.code);
              return (
                <div
                  key={method.code}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 4px',
                    borderBottom: `1px solid ${theme.divider}`,
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(method.code)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '13px', fontWeight: on ? 600 : 400 }}>
                      {lang === 'ar' ? method.ar : method.en}
                    </span>
                    <span style={{ fontSize: '11.5px', color: theme.textSubtle }}>
                      {lang === 'ar' ? method.en : method.ar}
                    </span>
                  </label>

                  {on && (
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11.5px',
                        color: defaultMethod === method.code ? theme.text : theme.textMuted,
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name="default-payment-method"
                        checked={defaultMethod === method.code}
                        onChange={() => {
                          setDefaultMethod(method.code);
                          setSaved(false);
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                      {t('Usual')}
                    </label>
                  )}
                </div>
              );
            })}
          </div>

          {chosen.length === 0 && (
            <div style={{ marginTop: '12px', fontSize: '12.5px', color: theme.badDeep, fontWeight: 600 }}>
              {t('Choose at least one way of being paid.')}
            </div>
          )}
        </div>
      </Card>

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          marginTop: '18px',
          padding: '14px 16px',
          background: theme.surface,
          border: `1px solid ${dirty ? theme.nav : theme.dividerSoft}`,
          borderRadius: theme.radiusCard,
          boxShadow: theme.shadowCard,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          disabled={busy || !dirty || chosen.length === 0}
          onClick={() => {
            void onSave(chosen, defaultMethod).then(
              () => setSaved(true),
              () => setSaved(false),
            );
          }}
          style={{
            background: busy || !dirty || chosen.length === 0 ? theme.borderStrong : theme.nav,
            color: '#fff',
            border: 0,
            borderRadius: theme.radiusControl,
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: busy || !dirty || chosen.length === 0 ? 'default' : 'pointer',
          }}
        >
          {busy ? t('Saving…') : t('Save changes')}
        </button>

        <span style={{ fontSize: '12px', color: theme.textMuted }}>
          {busy ? '' : dirty ? t('Not saved yet.') : saved ? t('Saved.') : t('Everything here is saved.')}
        </span>

        {error && !busy && (
          <span style={{ fontSize: '12px', color: theme.badDeep, fontWeight: 600 }}>{t(error)}</span>
        )}
      </div>
    </div>
  );
}
